// Web Track page — combines Now (today's check-offs), Cycles, and Fasting.

import { COMPOUND_BY_ID } from '../lib/compounds';
import { loadStacks } from '../lib/stack_store';
import { toggleItem, isItemChecked, todayKey } from '../lib/tracking_store';
import { loadStarts, setStart, clearStart, loadCustom, upsertCustom, removeCustom, parseCyclePattern, phaseFor, isDisclaimerDismissed, dismissDisclaimer, newCustomId, type CustomCycle } from '../lib/cycle_store';
import { loadActive as fastingActive, startFast, endFast } from '../lib/fasting_store';
import { loadToday as loadTodayConsumption } from '../lib/consumption_store';

type Tab = 'now' | 'cycles' | 'fasting';
let _tab: Tab = 'now';

export function renderTrack() {
  const app = document.getElementById('app')!;
  app.innerHTML = `
    <div class="detail-header">
      <h1>Track</h1>
      <p class="sci">Daily check-offs, cycles, and fasting — all local to this browser.</p>
    </div>

    <div class="track-tabs" style="display:flex;gap:6px;margin-bottom:14px;border-bottom:1px solid var(--line);">
      ${tabBtn('now', 'Now')}
      ${tabBtn('cycles', 'Cycles')}
      ${tabBtn('fasting', 'Fasting')}
    </div>

    <div id="track-body"></div>
  `;
  wireTabs();
  paintBody();
}

function tabBtn(id: Tab, label: string): string {
  const active = _tab === id;
  return `<button class="track-tab" data-tab="${id}"
            style="background:transparent;border:0;padding:10px 14px;color:${active ? 'var(--acc)' : 'var(--dim)'};
                   border-bottom:2px solid ${active ? 'var(--acc)' : 'transparent'};font-weight:700;cursor:pointer;">
            ${label}
          </button>`;
}

function wireTabs() {
  document.querySelectorAll('.track-tab').forEach((b) => {
    b.addEventListener('click', () => {
      _tab = (b as HTMLElement).dataset.tab as Tab;
      renderTrack();
    });
  });
}

function paintBody() {
  const body = document.getElementById('track-body');
  if (!body) return;
  if (_tab === 'now')     { body.innerHTML = renderNow(); wireNow(); }
  if (_tab === 'cycles')  { body.innerHTML = renderCycles(); wireCycles(); }
  if (_tab === 'fasting') { body.innerHTML = renderFasting(); wireFasting(); }
}

/* ----- NOW: today's check-offs ----- */

function renderNow(): string {
  const stacks = loadStacks();
  const today = todayKey();
  if (stacks.length === 0) {
    return `<div style="padding:20px;background:var(--bg-2);border:1px solid var(--line);border-radius:10px;color:var(--dim);">No stacks yet. <a href="/stacks">Create one</a> to start tracking.</div>`;
  }
  const stackHtml = stacks.map((s) => {
    if (s.items.length === 0) return '';
    return `
      <section class="detail-section" data-stack="${esc(s.id)}">
        <h3>${esc(s.name)}</h3>
        <div style="display:flex;flex-direction:column;gap:6px;">
          ${s.items.filter((it) => it.active).map((it) => {
            const c = COMPOUND_BY_ID[it.compound_id];
            const name = c ? c.display_name : it.compound_id;
            const checked = isItemChecked(s.id, it.id);
            return `
              <label style="display:flex;align-items:center;gap:10px;padding:10px;background:var(--bg-3);border-radius:6px;cursor:pointer;${checked ? 'opacity:0.7;' : ''}">
                <input type="checkbox" class="now-check" data-stack="${esc(s.id)}" data-item="${esc(it.id)}" ${checked ? 'checked' : ''} />
                <span style="flex:1;${checked ? 'text-decoration:line-through;color:var(--dim);' : ''}">${esc(name)}${it.dose_value != null ? ` <span style="color:var(--dim);font-size:12px;">${it.dose_value} ${esc(it.unit)}</span>` : ''}${it.timing ? ` <span style="color:var(--dim);font-size:12px;">· ${esc(it.timing)}</span>` : ''}</span>
              </label>
            `;
          }).join('')}
        </div>
      </section>
    `;
  }).join('');
  return stackHtml +
    `<section class="detail-section">
      <h3>Today's quick log <a href="/quick-log" style="color:var(--acc);font-size:13px;font-weight:600;text-decoration:none;margin-left:8px;">⚡ Add</a></h3>
      <div id="today-consumption" style="display:flex;flex-direction:column;gap:6px;">Loading…</div>
    </section>
    <p style="color:var(--dim);font-size:12px;">Today: ${today}</p>`;
}

async function paintTodayConsumption() {
  const wrap = document.getElementById('today-consumption');
  if (!wrap) return;
  const today = await loadTodayConsumption();
  if (today.length === 0) {
    wrap.innerHTML = `<div style="color:var(--dim);font-size:13px;">Nothing quick-logged today. Tap ⚡ Add above to log something you just consumed.</div>`;
    return;
  }
  // Group by source/product name so multiple ingredients from one scan cluster.
  const groups: Record<string, typeof today> = {};
  for (const e of today) {
    const k = e.source || 'Direct entries';
    if (!groups[k]) groups[k] = [];
    groups[k].push(e);
  }
  wrap.innerHTML = Object.entries(groups).map(([groupName, entries]) => {
    const items = entries.map((e) => {
      const c = COMPOUND_BY_ID[e.compound_id];
      const name = c ? c.display_name : e.compound_id;
      const t = new Date(e.consumed_at);
      const tStr = `${t.getHours().toString().padStart(2, '0')}:${t.getMinutes().toString().padStart(2, '0')}`;
      const qtyStr = e.qty != null ? `${e.qty} × ` : '';
      const unitStr = e.qty_unit ? ` ${esc(e.qty_unit)}` : '';
      return `<div style="display:flex;gap:10px;font-size:13px;padding:4px 0;">
        <span style="color:var(--dim);width:48px;font-size:11px;">${tStr}</span>
        <span style="flex:1;">${qtyStr}${esc(name)}${unitStr}</span>
      </div>`;
    }).join('');
    return entries.length === 1
      ? `<div style="padding:10px;background:var(--bg-3);border-radius:6px;">${items}</div>`
      : `<div style="padding:10px;background:var(--bg-3);border-radius:6px;">
          <div style="font-weight:700;font-size:12px;color:var(--acc);margin-bottom:4px;">${esc(groupName)}</div>
          ${items}
        </div>`;
  }).join('');
}

function wireNow() {
  paintTodayConsumption();
  document.querySelectorAll('.now-check').forEach((cb) => {
    cb.addEventListener('change', () => {
      const sId = (cb as HTMLElement).dataset.stack;
      const iId = (cb as HTMLElement).dataset.item;
      if (sId && iId) {
        toggleItem(sId, iId);
        paintBody();
      }
    });
  });
}

/* ----- CYCLES ----- */

function renderCycles(): string {
  const stacks = loadStacks();
  const starts = loadStarts();
  const custom = loadCustom();
  const disclaimerDismissed = isDisclaimerDismissed();

  // Auto cycles: compounds with cycle_required across all active items
  const seen = new Set<string>();
  const autos: { id: string; name: string; pattern: string; onDays: number | null; offDays: number | null; start: Date | undefined }[] = [];
  for (const s of stacks) {
    for (const it of s.items) {
      if (!it.active) continue;
      if (seen.has(it.compound_id)) continue;
      const c = COMPOUND_BY_ID[it.compound_id];
      if (!c || !c.cycle_required) continue;
      const parsed = parseCyclePattern(c.cycle_pattern);
      autos.push({
        id: c.id,
        name: c.display_name,
        pattern: c.cycle_pattern || '',
        onDays: parsed ? parsed[0] : null,
        offDays: parsed ? parsed[1] : null,
        start: starts[c.id],
      });
      seen.add(c.id);
    }
  }
  autos.sort((a, b) => a.name.localeCompare(b.name));

  return `
    ${disclaimerDismissed ? '' : `
      <div style="background:rgba(255,179,71,0.18);border:1px solid var(--warn);border-radius:10px;padding:12px;margin-bottom:14px;">
        <p style="margin:0 0 8px;color:#FFD08A;font-size:13px;line-height:1.5;">
          Cycle patterns shown here are starting points from research and community wisdom — every body responds differently.
          Defaults are meant to be edited to your own protocol. Combinations of compounds can change what works.
          This is your tracker for your own data. Not medical advice.
        </p>
        <div style="text-align:right;"><button id="cycle-dismiss" class="btn" style="padding:4px 12px;font-size:12px;">Got it</button></div>
      </div>
    `}

    <section class="detail-section">
      <h3>From your stacks</h3>
      <p style="color:var(--dim);font-size:12px;">${autos.length} compound${autos.length === 1 ? '' : 's'} in your active stacks need cycling.</p>
      ${autos.length === 0 ? `<div style="padding:14px;background:var(--bg-2);border:1px solid var(--line);border-radius:10px;color:var(--dim);font-size:13px;">Nothing here yet. Add a stack item with a compound that requires cycling (huperzine-a, ashwagandha, melatonin, etc.) and it shows up here.</div>` : ''}
      <div style="display:flex;flex-direction:column;gap:10px;">
        ${autos.map(autoCard).join('')}
      </div>
    </section>

    <section class="detail-section">
      <h3>Custom cycles</h3>
      <p style="color:var(--dim);font-size:12px;">Your own protocols — caffeine T-break, deload weeks, sauna, anything you want to time.</p>
      <button id="add-custom" class="btn btn-primary" style="margin-bottom:10px;">+ Add custom cycle</button>
      ${custom.length === 0 ? `<div style="padding:14px;background:var(--bg-2);border:1px solid var(--line);border-radius:10px;color:var(--dim);font-size:13px;">No custom cycles yet.</div>` : ''}
      <div style="display:flex;flex-direction:column;gap:10px;">
        ${custom.map(customCard).join('')}
      </div>
    </section>
  `;
}

function autoCard(a: { id: string; name: string; pattern: string; onDays: number | null; offDays: number | null; start: Date | undefined }): string {
  const hasPattern = a.onDays != null && a.offDays != null;
  const ph = a.start && hasPattern ? phaseFor(a.start, a.onDays!, a.offDays!) : null;
  return `
    <div style="background:var(--bg-2);border:1px solid var(--line);border-radius:10px;padding:12px;">
      <div style="display:flex;align-items:center;gap:10px;">
        <div style="flex:1;font-weight:700;">${esc(a.name)}</div>
        ${ph ? phasePill(ph) : ''}
      </div>
      <div style="color:var(--dim);font-size:12px;margin-top:4px;">${esc(a.pattern || 'Cycle pattern not specified')}</div>
      <div style="margin-top:8px;display:flex;align-items:center;gap:8px;">
        ${a.start ? `
          <div style="flex:1;color:var(--dim);font-size:11px;">Started ${a.start.toISOString().slice(0, 10)} · day ${Math.floor((Date.now() - a.start.getTime()) / 86400000) + 1}</div>
          <button class="btn cycle-reset" data-id="${esc(a.id)}" style="padding:4px 10px;font-size:11px;">Reset</button>
          <button class="btn cycle-clear" data-id="${esc(a.id)}" style="padding:4px 10px;font-size:11px;color:#E25C5C;border-color:#E25C5C;">Clear</button>
        ` : (hasPattern
          ? `<button class="btn btn-primary cycle-start" data-id="${esc(a.id)}" style="padding:6px 12px;font-size:12px;">Start cycle today</button>`
          : `<div style="color:var(--warn);font-size:11px;">Pattern not auto-parseable. Add a custom cycle below if you want to track it.</div>`)}
      </div>
    </div>
  `;
}

function customCard(c: CustomCycle): string {
  const ph = phaseFor(new Date(c.started_at), c.on_days, c.off_days);
  return `
    <div data-id="${esc(c.id)}" class="custom-card" style="background:var(--bg-2);border:1px solid var(--line);border-radius:10px;padding:12px;cursor:pointer;">
      <div style="display:flex;align-items:center;gap:10px;">
        <div style="flex:1;font-weight:700;">${esc(c.label)}</div>
        ${phasePill(ph)}
      </div>
      <div style="color:var(--dim);font-size:12px;margin-top:4px;">${c.on_days} on, ${c.off_days} off · started ${c.started_at.slice(0, 10)}</div>
      ${c.notes ? `<div style="color:var(--ink);font-size:12px;margin-top:4px;">${esc(c.notes)}</div>` : ''}
    </div>
  `;
}

function phasePill(ph: { on_phase: boolean; days_remaining: number; label: string }): string {
  const color = ph.on_phase ? 'var(--acc)' : 'var(--warn)';
  return `<span style="padding:2px 8px;background:rgba(95,196,128,${ph.on_phase ? '0.2' : '0'});color:${color};border:1px solid ${color};border-radius:10px;font-size:11px;font-weight:700;">${ph.label} · ${ph.days_remaining}d left</span>`;
}

function wireCycles() {
  document.getElementById('cycle-dismiss')?.addEventListener('click', () => { dismissDisclaimer(); paintBody(); });
  document.querySelectorAll('.cycle-start').forEach((b) => {
    b.addEventListener('click', async () => {
      const id = (b as HTMLElement).dataset.id;
      if (id) { await setStart(id, new Date()); paintBody(); }
    });
  });
  document.querySelectorAll('.cycle-reset').forEach((b) => {
    b.addEventListener('click', async () => {
      const id = (b as HTMLElement).dataset.id;
      if (id) { await setStart(id, new Date()); paintBody(); }
    });
  });
  document.querySelectorAll('.cycle-clear').forEach((b) => {
    b.addEventListener('click', async () => {
      const id = (b as HTMLElement).dataset.id;
      if (id) { await clearStart(id); paintBody(); }
    });
  });
  document.getElementById('add-custom')?.addEventListener('click', () => {
    openCustomCycleModal(null);
  });
  document.querySelectorAll('.custom-card').forEach((card) => {
    card.addEventListener('click', async () => {
      const id = (card as HTMLElement).dataset.id;
      if (!id) return;
      const existing = (await loadCustom()).find((c) => c.id === id);
      if (existing) openCustomCycleModal(existing);
    });
  });
}

function openCustomCycleModal(existing: CustomCycle | null) {
  const editing = existing != null;
  const back = document.createElement('div');
  back.className = 'modal-back';
  back.innerHTML = `
    <div class="modal-box">
      <h2>${editing ? 'Edit cycle' : 'Add custom cycle'}</h2>
      <p class="small">Cycles you track yourself. Examples: caffeine T-break, sauna protocol, pre-workout deload, sleep schedule.</p>
      <label class="small" style="display:block;margin-top:12px;">Label
        <input id="cc-label" type="text" value="${escapeAttr(existing?.label ?? '')}" placeholder="e.g. Caffeine T-break"
          style="display:block;width:100%;margin-top:4px;padding:8px 10px;background:var(--bg-3);border:1px solid var(--line);border-radius:6px;color:var(--ink);font-size:14px;" />
      </label>
      <div style="display:flex;gap:10px;margin-top:10px;">
        <label class="small" style="flex:1;">On (days)
          <input id="cc-on" type="number" min="1" value="${existing?.on_days ?? 14}"
            style="display:block;width:100%;margin-top:4px;padding:8px 10px;background:var(--bg-3);border:1px solid var(--line);border-radius:6px;color:var(--ink);font-size:14px;" />
        </label>
        <label class="small" style="flex:1;">Off (days)
          <input id="cc-off" type="number" min="0" value="${existing?.off_days ?? 7}"
            style="display:block;width:100%;margin-top:4px;padding:8px 10px;background:var(--bg-3);border:1px solid var(--line);border-radius:6px;color:var(--ink);font-size:14px;" />
        </label>
      </div>
      <label class="small" style="display:block;margin-top:10px;">Notes (optional)
        <textarea id="cc-notes" rows="3" placeholder=""
          style="display:block;width:100%;margin-top:4px;padding:8px 10px;background:var(--bg-3);border:1px solid var(--line);border-radius:6px;color:var(--ink);font-size:14px;resize:vertical;">${escapeText(existing?.notes ?? '')}</textarea>
      </label>
      <div id="cc-err" class="small" style="color:#FFB347;margin-top:8px;min-height:14px;"></div>
      <div style="display:flex;gap:10px;justify-content:space-between;margin-top:14px;align-items:center;">
        <div>
          ${editing ? `<button id="cc-delete" class="btn" style="color:#E25C5C;border-color:#5a2a2a;">Delete</button>` : ''}
        </div>
        <div style="display:flex;gap:10px;">
          <button id="cc-cancel" class="btn">Cancel</button>
          <button id="cc-save" class="btn btn-primary">${editing ? 'Save' : 'Add cycle'}</button>
        </div>
      </div>
    </div>
  `;
  document.body.appendChild(back);
  const close = () => back.remove();
  document.getElementById('cc-cancel')?.addEventListener('click', close);
  back.addEventListener('click', (e) => { if (e.target === back) close(); });
  document.getElementById('cc-save')?.addEventListener('click', async () => {
    const label = (document.getElementById('cc-label') as HTMLInputElement).value.trim();
    const on = parseInt((document.getElementById('cc-on') as HTMLInputElement).value || '0');
    const off = parseInt((document.getElementById('cc-off') as HTMLInputElement).value || '0');
    const notes = (document.getElementById('cc-notes') as HTMLTextAreaElement).value.trim();
    const err = document.getElementById('cc-err')!;
    if (!label) { err.textContent = 'Label is required.'; return; }
    if (!on || on < 1) { err.textContent = 'On days must be 1 or more.'; return; }
    if (off < 0) { err.textContent = 'Off days cannot be negative.'; return; }
    await upsertCustom({
      id: existing?.id ?? newCustomId(),
      label,
      on_days: on,
      off_days: off,
      started_at: existing?.started_at ?? new Date().toISOString(),
      notes: notes || null,
    });
    close();
    paintBody();
  });
  if (editing) {
    document.getElementById('cc-delete')?.addEventListener('click', async () => {
      await removeCustom(existing!.id);
      close();
      paintBody();
    });
  }
  setTimeout(() => (document.getElementById('cc-label') as HTMLInputElement)?.focus(), 30);
}

function escapeAttr(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}
function escapeText(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

/* ----- FASTING ----- */

const PRESETS = [
  { protocol: '12:12', hours: 12 },
  { protocol: '14:10', hours: 14 },
  { protocol: '16:8',  hours: 16 },
  { protocol: '18:6',  hours: 18 },
  { protocol: '20:4',  hours: 20 },
  { protocol: 'OMAD',  hours: 23 },
];

let _tick: number | null = null;

function renderFasting(): string {
  const a = fastingActive();
  if (!a) {
    return `
      <section class="detail-section">
        <h3>Start a fast</h3>
        <p style="color:var(--dim);font-size:13px;">Pick a target window. Timer ticks until you tap End.</p>
        <div style="display:flex;flex-wrap:wrap;gap:8px;margin-top:10px;">
          ${PRESETS.map((p) => `<button class="btn fast-start" data-protocol="${esc(p.protocol)}" data-hours="${p.hours}">${esc(p.protocol)} <span style="color:var(--dim);font-size:12px;">(${p.hours}h)</span></button>`).join('')}
        </div>
      </section>
    `;
  }
  const start = new Date(a.started_at);
  const elapsedMs = Date.now() - start.getTime();
  const targetMs = a.target_hours * 3600000;
  const remainingMs = Math.max(0, targetMs - elapsedMs);
  const pct = Math.min(100, (elapsedMs / targetMs) * 100);
  const fmt = (ms: number) => {
    const totalMins = Math.floor(ms / 60000);
    const h = Math.floor(totalMins / 60);
    const m = totalMins % 60;
    return `${h}h ${m}m`;
  };
  return `
    <section class="detail-section">
      <h3>${esc(a.protocol)}</h3>
      <p style="color:var(--dim);font-size:13px;">Started ${start.toLocaleString()}</p>
      <div style="margin:18px 0;display:flex;flex-direction:column;align-items:center;">
        <div style="width:200px;height:200px;border:8px solid var(--bg-3);border-top-color:var(--acc);border-radius:50%;display:flex;align-items:center;justify-content:center;
                    transform:rotate(${(pct / 100) * 360}deg);transition:transform 0.5s linear;"></div>
        <div style="margin-top:-110px;text-align:center;color:var(--ink);font-size:24px;font-weight:700;pointer-events:none;">${fmt(elapsedMs)}</div>
        <div style="margin-top:60px;color:var(--dim);font-size:13px;">${pct >= 100 ? 'TARGET REACHED' : fmt(remainingMs) + ' left'}</div>
      </div>
      <div style="display:flex;gap:10px;">
        <button id="fast-end-good" class="btn btn-primary" style="flex:1;">End (logged)</button>
        <button id="fast-end-abandon" class="btn" style="flex:1;color:#E25C5C;border-color:#E25C5C;">Abandon</button>
      </div>
    </section>
  `;
}

function wireFasting() {
  if (_tick) { clearInterval(_tick); _tick = null; }
  document.querySelectorAll('.fast-start').forEach((b) => {
    b.addEventListener('click', () => {
      const p = (b as HTMLElement).dataset.protocol || 'Custom';
      const h = +((b as HTMLElement).dataset.hours || '16');
      (async () => { await startFast(p, h); paintBody(); })();
    });
  });
  document.getElementById('fast-end-good')?.addEventListener('click', async () => { await endFast(true); paintBody(); });
  document.getElementById('fast-end-abandon')?.addEventListener('click', async () => { await endFast(false); paintBody(); });
  if (fastingActive()) {
    _tick = window.setInterval(() => {
      // Lightweight tick: only re-render fasting view every 30s
      if (_tab === 'fasting') paintBody();
    }, 30000) as unknown as number;
  }
}

function esc(s: string) {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}
